import { memo, ReactElement, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { RichTextEditor } from 'components/common/RichTextEditor';
import { CollapsibleRichTextComment } from 'components/inspection/CollapsibleRichTextComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { TextChecklistAnswerModel } from 'shared/domain/template/inspectionTemplateModel';
import { answerLabelSx, requiredIndicatorSx, getAnswerFieldCardSx, answerFieldBodySx } from './styles';

interface TextAnswerInputProps {
  answerConfig: TextChecklistAnswerModel;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  hideLabel?: boolean;
}

function TextAnswerInputComponent({
  answerConfig,
  value,
  onChange,
  onBlur,
  disabled,
  hideLabel = false,
}: TextAnswerInputProps): ReactElement {
  const intl = useIntl();
  const handleChange = useCallback(
    (html: string) => {
      onChange(html);
    },
    [onChange]
  );

  return (
    <Box sx={hideLabel ? answerFieldBodySx : getAnswerFieldCardSx(answerConfig.type)}>
      {answerConfig.label && !hideLabel ? (
        <Typography component='p' sx={answerLabelSx}>
          {answerConfig.label}
          {answerConfig.isRequired && (
            <Box component='span' sx={requiredIndicatorSx}>*</Box>
          )}
        </Typography>
      ) : null}
      {/* Same collapsible shell as the item comment: one line until focused,
          formatting toolbar behind the "Tт" toggle. */}
      <CollapsibleRichTextComment
        value={value ?? ''}
        disabled={Boolean(disabled)}
        labelVariant={InspectionCommentLabelVariant.section}
      >
        <RichTextEditor
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          dense
          placeholder={formatTranslation(
            intl,
            InspectionTranslationKeys.textAnswerPlaceholder
          )}
          fieldName={`text-answer-${answerConfig._id}`}
          dataQa={`protocol-text-answer-${answerConfig._id}`}
        />
      </CollapsibleRichTextComment>
    </Box>
  );
}

export const TextAnswerInput = memo(TextAnswerInputComponent);
